import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { api, ApiError } from '../api/client.js'
import { useAuth } from '../auth/AuthContext.js'

type SubView = 'worklist' | 'invoices' | 'receivables' | 'collections'

const SUB_NAV: Array<{ key: SubView; label: string }> = [
  { key: 'worklist', label: 'Invoicing Worklist' },
  { key: 'invoices', label: 'Invoices' },
  { key: 'receivables', label: 'Receivables' },
  { key: 'collections', label: 'Collections' },
]

/**
 * Stage 8: invoicing, receivables ledger/ageing, receipts, and the
 * Collection Dashboard — one page, four sub-views, same pattern as the
 * top-level app nav (plain state, no router).
 */
export function BillingPage() {
  const [sub, setSub] = useState<SubView>('worklist')

  return (
    <div>
      <nav className="tf-tabs" style={{ marginBottom: '1rem' }}>
        {SUB_NAV.map((item) => (
          <button
            key={item.key}
            type="button"
            className={sub === item.key ? 'tf-tab-active' : ''}
            onClick={() => setSub(item.key)}
          >
            {item.label}
          </button>
        ))}
      </nav>

      {sub === 'worklist' && <WorklistTab />}
      {sub === 'invoices' && <InvoicesTab />}
      {sub === 'receivables' && <ReceivablesTab />}
      {sub === 'collections' && <CollectionsTab />}
    </div>
  )
}

// ---------------------------------------------------------------------------
interface WorklistRow {
  trip_id: string
  trip_no: string
  booking_id: string
  origin: string
  destination: string
  freight_amount: string
  customer_id: string
  customer_name: string
  uniform_gst_treatment: boolean
}

function WorklistTab() {
  const { currentBranchId } = useAuth()
  const [rows, setRows] = useState<WorklistRow[]>([])
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [warnings, setWarnings] = useState<string[]>([])

  const reload = useCallback(async () => {
    const res = await api.get<WorklistRow[]>('/invoicing/worklist')
    setRows(res)
    setSelected(new Set())
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  // Grouped by customer so generating one invoice only ever mixes trips that
  // already belong to the same customer (Stage 8 §2.4).
  const byCustomer = new Map<string, WorklistRow[]>()
  for (const row of rows) {
    const list = byCustomer.get(row.customer_id) ?? []
    list.push(row)
    byCustomer.set(row.customer_id, list)
  }

  function toggle(tripId: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(tripId)) next.delete(tripId)
      else next.add(tripId)
      return next
    })
  }

  async function generate(customerId: string) {
    if (!currentBranchId) {
      setError('Select a branch first')
      return
    }
    const tripIds = [...selected].filter((id) => byCustomer.get(customerId)?.some((r) => r.trip_id === id))
    if (!tripIds.length) return
    setSaving(true)
    setError(null)
    setWarnings([])
    try {
      const invoice = await api.post<{ warnings?: string[] }>('/invoices', {
        customer_id: customerId,
        branch_id: currentBranchId,
        trip_ids: tripIds,
      })
      if (invoice.warnings?.length) setWarnings(invoice.warnings)
      await reload()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to generate invoice')
    } finally {
      setSaving(false)
    }
  }

  if (!rows.length) return <p className="tf-note">No trips are currently ready to invoice.</p>

  return (
    <div>
      {error && <p className="tf-error">{error}</p>}
      {warnings.map((w) => (
        <p key={w} className="tf-note" style={{ color: 'var(--tf-warn, #b45309)' }}>
          ⚠ {w}
        </p>
      ))}
      {[...byCustomer.entries()].map(([customerId, customerRows]) => (
        <div key={customerId} className="tf-card" style={{ marginBottom: '1rem' }}>
          <h3>{customerRows[0]!.customer_name}</h3>
          <table className="tf-table">
            <thead>
              <tr>
                <th></th>
                <th>Trip</th>
                <th>Route</th>
                <th>Freight</th>
              </tr>
            </thead>
            <tbody>
              {customerRows.map((r) => (
                <tr key={r.trip_id}>
                  <td>
                    <input type="checkbox" checked={selected.has(r.trip_id)} onChange={() => toggle(r.trip_id)} />
                  </td>
                  <td>{r.trip_no}</td>
                  <td>
                    {r.origin} → {r.destination}
                  </td>
                  <td>₹{r.freight_amount}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="tf-form-actions">
            <button
              type="button"
              className="tf-button-primary"
              disabled={saving || ![...selected].some((id) => customerRows.some((r) => r.trip_id === id))}
              onClick={() => void generate(customerId)}
            >
              {saving ? 'Generating…' : 'Generate Invoice'}
            </button>
          </div>
        </div>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
interface InvoiceRow {
  id: string
  invoice_no: string
  invoice_date: string
  customer_name: string
  gst_treatment: 'RCM' | 'ForwardCharge'
  total_amount: string
  received?: string
  outstanding?: string
  payment_count?: number
  status: string
}

function InvoicesTab() {
  const [rows, setRows] = useState<InvoiceRow[]>([])
  const [openId, setOpenId] = useState<string | null>(null)
  const [receiptFor, setReceiptFor] = useState<string | null>(null)

  const reload = useCallback(async () => {
    const res = await api.get<{ rows: InvoiceRow[] }>('/invoices')
    setRows(res.rows)
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  return (
    <div>
      <table className="tf-table">
        <thead>
          <tr>
            <th>Invoice No.</th>
            <th>Date</th>
            <th>Customer</th>
            <th>GST</th>
            <th>Total</th>
            <th>Received</th>
            <th>Balance</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} onClick={() => setOpenId(r.id)} style={{ cursor: 'pointer' }}>
              <td>{r.invoice_no}</td>
              <td>{new Date(r.invoice_date).toLocaleDateString()}</td>
              <td>{r.customer_name}</td>
              <td>{r.gst_treatment === 'RCM' ? 'RCM' : 'Forward Charge'}</td>
              <td>₹{r.total_amount}</td>
              <td>
                {Number(r.received ?? 0) > 0 ? (
                  <a
                    href="#"
                    title="View part payment receipt"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      setReceiptFor(r.id)
                    }}
                  >
                    ₹{r.received}
                  </a>
                ) : (
                  '—'
                )}
              </td>
              <td>₹{r.outstanding ?? r.total_amount}</td>
              <td>{r.status}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {receiptFor && <PartPaymentReceiptModal invoiceId={receiptFor} onClose={() => setReceiptFor(null)} />}

      {openId && (
        <InvoiceDetail
          invoiceId={openId}
          onClose={() => setOpenId(null)}
          onChanged={() => {
            void reload()
          }}
        />
      )}
    </div>
  )
}

interface InvoiceDetailData extends InvoiceRow {
  taxable_value: string
  tax_amount: string
  gst_rate: string | null
  items: { id: string; description: string; amount: string; trip_no: string | null }[]
  payments: PaymentLine[]
}

interface PaymentLine {
  receipt_id: string
  receipt_no: string
  receipt_date: string
  mode: string
  reference_no: string | null
  amount: string
  cumulative: string
  balance_after: string
}

function PartPaymentReceiptModal({ invoiceId, onClose }: { invoiceId: string; onClose: () => void }) {
  const [data, setData] = useState<{
    invoice: { invoice_no: string; invoice_date: string; customer_name: string; total_amount: string }
    tenant: { company_name: string; letterhead_phones: string[] }
    branch: { name: string; address: string | null; state: string | null }
    payments: PaymentLine[]
    received: string
    outstanding: string
  } | null>(null)

  useEffect(() => {
    void api.get<NonNullable<typeof data>>(`/invoices/${invoiceId}/print`).then(setData)
  }, [invoiceId])

  if (!data) return null
  return (
    <div className="tf-slideover-backdrop" onClick={onClose}>
      <div className="tf-print-sheet" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '50rem', maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="tf-no-print tf-form-actions" style={{ marginBottom: '1rem' }}>
          <button type="button" className="tf-button-primary" onClick={() => window.print()}>
            Print
          </button>
          <button type="button" onClick={onClose}>
            Close
          </button>
        </div>
        <div className="tf-print-header">
          <div>
            <h1>{data.tenant.company_name}</h1>
            <div className="tf-note">
              {data.branch.name}
              {data.branch.address ? `, ${data.branch.address}` : ''}
              {data.branch.state ? `, ${data.branch.state}` : ''}
            </div>
            {data.tenant.letterhead_phones?.length > 0 && <div className="tf-note">Ph: {data.tenant.letterhead_phones.join(', ')}</div>}
          </div>
          <div className="tf-note" style={{ textAlign: 'right' }}>
            <strong>Part Payment Receipt</strong>
            <br />
            Invoice: {data.invoice.invoice_no}
            <br />
            Date: {new Date(data.invoice.invoice_date).toLocaleDateString()}
          </div>
        </div>
        <p>
          <strong>Customer:</strong> {data.invoice.customer_name}
        </p>
        <table className="tf-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Date</th>
              <th>Receipt No.</th>
              <th>Mode</th>
              <th>Reference</th>
              <th>Paid</th>
              <th>Total paid</th>
              <th>Balance</th>
            </tr>
          </thead>
          <tbody>
            {data.payments.map((p, i) => (
              <tr key={p.receipt_id}>
                <td>{i + 1}</td>
                <td>{new Date(p.receipt_date).toLocaleDateString()}</td>
                <td>{p.receipt_no}</td>
                <td>{p.mode}</td>
                <td>{p.reference_no ?? '—'}</td>
                <td>₹{p.amount}</td>
                <td>₹{p.cumulative}</td>
                <td>₹{p.balance_after}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p>
          Invoice total: ₹{data.invoice.total_amount}
          <br />
          Received so far: ₹{data.received}
          <br />
          <strong>Balance due: ₹{data.outstanding}</strong>
        </p>
      </div>
    </div>
  )
}

function InvoiceDetail({
  invoiceId,
  onClose,
  onChanged,
}: {
  invoiceId: string
  onClose: () => void
  onChanged: () => void
}) {
  const [invoice, setInvoice] = useState<InvoiceDetailData | null>(null)
  const [receiptFormOpen, setReceiptFormOpen] = useState(false)
  const [partReceiptOpen, setPartReceiptOpen] = useState(false)

  const reload = useCallback(async () => {
    setInvoice(await api.get<InvoiceDetailData>(`/invoices/${invoiceId}`))
  }, [invoiceId])

  useEffect(() => {
    void reload()
  }, [reload])

  if (!invoice) return null

  return (
    <div className="tf-slideover-backdrop" onClick={onClose}>
      <div className="tf-slideover" onClick={(e) => e.stopPropagation()}>
        <h2>{invoice.invoice_no}</h2>
        <p className="tf-note">
          {invoice.customer_name} — {invoice.gst_treatment === 'RCM' ? 'GST payable under RCM by recipient' : `Forward Charge @ ${invoice.gst_rate}%`}
        </p>

        <table className="tf-table">
          <thead>
            <tr>
              <th>Trip</th>
              <th>Description</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {invoice.items.map((i) => (
              <tr key={i.id}>
                <td>{i.trip_no ?? '—'}</td>
                <td>{i.description}</td>
                <td>₹{i.amount}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <p>
          Taxable value: ₹{invoice.taxable_value}
          {Number(invoice.tax_amount) > 0 && (
            <>
              <br />
              Tax: ₹{invoice.tax_amount}
            </>
          )}
          <br />
          <strong>Total: ₹{invoice.total_amount}</strong>
        </p>
        <p className="tf-note">Status: {invoice.status}</p>

        {invoice.payments.length > 0 && (
          <>
            <p>
              Paid ₹{invoice.received} · <strong>Balance ₹{invoice.outstanding}</strong>
            </p>
            <table className="tf-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Receipt</th>
                  <th>Paid</th>
                  <th>Balance</th>
                </tr>
              </thead>
              <tbody>
                {invoice.payments.map((p) => (
                  <tr key={p.receipt_id}>
                    <td>{new Date(p.receipt_date).toLocaleDateString()}</td>
                    <td>{p.receipt_no}</td>
                    <td>₹{p.amount}</td>
                    <td>₹{p.balance_after}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="tf-form-actions">
              <button type="button" onClick={() => setPartReceiptOpen(true)}>
                Part payment receipt
              </button>
            </div>
          </>
        )}

        {invoice.status !== 'Paid' && (
          <div className="tf-form-actions">
            <button type="button" className="tf-button-primary" onClick={() => setReceiptFormOpen(true)}>
              Record Receipt
            </button>
          </div>
        )}
        <div className="tf-form-actions">
          <button type="button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>

      {partReceiptOpen && <PartPaymentReceiptModal invoiceId={invoiceId} onClose={() => setPartReceiptOpen(false)} />}
      {receiptFormOpen && (
        <ReceiptFormModal
          preselectedInvoiceId={invoiceId}
          onClose={() => setReceiptFormOpen(false)}
          onSaved={async () => {
            setReceiptFormOpen(false)
            await reload()
            onChanged()
          }}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
interface AgeingRow {
  customer_id: string
  customer_name: string
  outstanding: string
  bucket_0_30: string
  bucket_31_60: string
  bucket_61_90: string
  bucket_90_plus: string
}

function ReceivablesTab() {
  const [rows, setRows] = useState<AgeingRow[]>([])
  const [ledgerCustomerId, setLedgerCustomerId] = useState<string | null>(null)

  useEffect(() => {
    void api.get<AgeingRow[]>('/receivables/ageing').then(setRows)
  }, [])

  return (
    <div>
      <table className="tf-table">
        <thead>
          <tr>
            <th>Customer</th>
            <th>Outstanding</th>
            <th>0–30</th>
            <th>31–60</th>
            <th>61–90</th>
            <th>90+</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.customer_id} onClick={() => setLedgerCustomerId(r.customer_id)} style={{ cursor: 'pointer' }}>
              <td>{r.customer_name}</td>
              <td>₹{r.outstanding}</td>
              <td>₹{r.bucket_0_30}</td>
              <td>₹{r.bucket_31_60}</td>
              <td>₹{r.bucket_61_90}</td>
              <td>₹{r.bucket_90_plus}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {ledgerCustomerId && (
        <CustomerLedgerModal customerId={ledgerCustomerId} onClose={() => setLedgerCustomerId(null)} />
      )}
    </div>
  )
}

interface LedgerData {
  customer: { name: string }
  invoices: { id: string; invoice_no: string; invoice_date: string; total_amount: string; status: string }[]
  receipts: { id: string; receipt_no: string; receipt_date: string; amount: string; mode: string }[]
  outstanding: string
}

function CustomerLedgerModal({ customerId, onClose }: { customerId: string; onClose: () => void }) {
  const [ledger, setLedger] = useState<LedgerData | null>(null)

  useEffect(() => {
    void api.get<LedgerData>(`/customers/${customerId}/ledger`).then(setLedger)
  }, [customerId])

  if (!ledger) return null

  return (
    <div className="tf-slideover-backdrop" onClick={onClose}>
      <div className="tf-slideover" onClick={(e) => e.stopPropagation()}>
        <h2>{ledger.customer.name} — Ledger</h2>
        <p className="tf-note">Outstanding: ₹{ledger.outstanding}</p>

        <h3>Invoices</h3>
        <table className="tf-table">
          <thead>
            <tr>
              <th>No.</th>
              <th>Date</th>
              <th>Amount</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {ledger.invoices.map((i) => (
              <tr key={i.id}>
                <td>{i.invoice_no}</td>
                <td>{new Date(i.invoice_date).toLocaleDateString()}</td>
                <td>₹{i.total_amount}</td>
                <td>{i.status}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <h3>Receipts</h3>
        <table className="tf-table">
          <thead>
            <tr>
              <th>No.</th>
              <th>Date</th>
              <th>Amount</th>
              <th>Mode</th>
            </tr>
          </thead>
          <tbody>
            {ledger.receipts.map((r) => (
              <tr key={r.id}>
                <td>{r.receipt_no}</td>
                <td>{new Date(r.receipt_date).toLocaleDateString()}</td>
                <td>₹{r.amount}</td>
                <td>{r.mode}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="tf-form-actions">
          <button type="button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Stage 8 §4: receipt entry with allocation. Can be opened pre-set to one
// invoice (from Invoice Detail) or blank (a future "new receipt" entry
// point) — customerId/preselectedInvoiceId cover both.
function ReceiptFormModal({
  customerId,
  preselectedInvoiceId,
  onClose,
  onSaved,
}: {
  customerId?: string
  preselectedInvoiceId?: string
  onClose: () => void
  onSaved: () => void
}) {
  const [amount, setAmount] = useState('')
  const [mode, setMode] = useState<'Cash' | 'Bank' | 'UPI' | 'Cheque'>('Bank')
  const [referenceNo, setReferenceNo] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [resolvedCustomerId, setResolvedCustomerId] = useState(customerId ?? '')

  useEffect(() => {
    if (customerId || !preselectedInvoiceId) return
    // Opened from Invoice Detail without a customer prop — look it up.
    void api.get<{ customer_id: string }>(`/invoices/${preselectedInvoiceId}`).then((inv) => setResolvedCustomerId(inv.customer_id))
  }, [customerId, preselectedInvoiceId])

  async function submit(e: FormEvent) {
    e.preventDefault()
    setSaving(true)
    setError(null)
    try {
      await api.post('/receipts', {
        customer_id: resolvedCustomerId,
        amount,
        mode,
        reference_no: referenceNo || undefined,
        allocations: preselectedInvoiceId ? [{ invoice_id: preselectedInvoiceId, amount }] : undefined,
      })
      onSaved()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save receipt')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="tf-slideover-backdrop" onClick={onClose}>
      <div className="tf-slideover" onClick={(e) => e.stopPropagation()}>
        <h2>Record Receipt</h2>
        <form onSubmit={submit} className="tf-form">
          <label className="tf-field">
            <span>Amount</span>
            <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} required />
          </label>
          <label className="tf-field">
            <span>Mode</span>
            <select value={mode} onChange={(e) => setMode(e.target.value as typeof mode)}>
              <option value="Cash">Cash</option>
              <option value="Bank">Bank</option>
              <option value="UPI">UPI</option>
              <option value="Cheque">Cheque</option>
            </select>
          </label>
          <label className="tf-field">
            <span>Reference no. (optional)</span>
            <input value={referenceNo} onChange={(e) => setReferenceNo(e.target.value)} />
          </label>
          {error && <p className="tf-error">{error}</p>}
          <div className="tf-form-actions">
            <button type="submit" className="tf-button-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save Receipt'}
            </button>
            <button type="button" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
interface CollectionRow {
  id: string
  invoice_no: string
  customer_name: string
  balance: string
  days_overdue: number
  latest_note: string | null
}

function CollectionsTab() {
  const [rows, setRows] = useState<CollectionRow[]>([])
  const [noteFor, setNoteFor] = useState<string | null>(null)
  const [noteText, setNoteText] = useState('')
  const [saving, setSaving] = useState(false)

  const reload = useCallback(async () => {
    setRows(await api.get<CollectionRow[]>('/collections/dashboard'))
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  async function saveNote(invoiceId: string) {
    if (!noteText.trim()) return
    setSaving(true)
    try {
      await api.post(`/invoices/${invoiceId}/follow-up-note`, { note: noteText })
      setNoteText('')
      setNoteFor(null)
      await reload()
    } finally {
      setSaving(false)
    }
  }

  return (
    <table className="tf-table">
      <thead>
        <tr>
          <th>Invoice</th>
          <th>Customer</th>
          <th>Balance</th>
          <th>Days Overdue</th>
          <th>Latest Note</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id}>
            <td>{r.invoice_no}</td>
            <td>{r.customer_name}</td>
            <td>₹{r.balance}</td>
            <td>{r.days_overdue}</td>
            <td>{r.latest_note ?? '—'}</td>
            <td>
              {noteFor === r.id ? (
                <div style={{ display: 'flex', gap: '0.4rem' }}>
                  <input value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Add note…" />
                  <button type="button" disabled={saving} onClick={() => void saveNote(r.id)}>
                    Save
                  </button>
                </div>
              ) : (
                <button type="button" onClick={() => setNoteFor(r.id)}>
                  + Note
                </button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
